import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import {
  PersonalMediaResponse,
  listUserMedia,
  getAuthenticatedUserImageUrl,
} from '../services/personalMediaList';
import { useAuth } from './AuthContext';

interface PersonalMediaLibraryContextType {
  mediaData: PersonalMediaResponse;
  loading: boolean;
  error: string | null;
  currentPage: number;
  pageSize: number;
  totalPages: number;
  refresh: () => Promise<void>;
  getAuthenticatedImage: (imageUrl: string) => Promise<string>;
  goToPage: (page: number) => void;
}

const PersonalMediaLibraryContext = createContext<PersonalMediaLibraryContextType | undefined>(undefined);

export const PersonalMediaLibraryProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { session, loading: authLoading } = useAuth();
  const pageSize = 20;
  const [mediaData, setMediaData] = useState<PersonalMediaResponse>({ limit: 0, offset: 0, total: 0, images: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = Math.max(1, Math.ceil(mediaData.total / pageSize));

  const fetchMedia = useCallback(async (page: number) => {
    if (authLoading) {
      setLoading(true);
      return;
    }

    if (!session?.access_token) {
      setMediaData({ limit: 0, offset: 0, total: 0, images: [] });
      setError(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const offset = (page - 1) * pageSize;
      const data = await listUserMedia(pageSize, offset);
      setMediaData(data);
    } catch (err) {
      setError('Failed to load your media library');
      console.error('Error fetching personal media:', err);
    } finally {
      setLoading(false);
    }
  }, [authLoading, pageSize, session?.access_token]);

  useEffect(() => {
    fetchMedia(currentPage);
  }, [currentPage, fetchMedia]);

  const refresh = async () => {
    await fetchMedia(currentPage);
  };

  const getAuthenticatedImage = useCallback(async (imageUrl: string): Promise<string> => {
    return getAuthenticatedUserImageUrl(imageUrl);
  }, []);

  const goToPage = (page: number) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  return (
    <PersonalMediaLibraryContext.Provider value={{
      mediaData,
      loading,
      error,
      currentPage,
      pageSize,
      totalPages,
      refresh,
      getAuthenticatedImage,
      goToPage,
    }}>
      {children}
    </PersonalMediaLibraryContext.Provider>
  );
};

export const usePersonalMediaLibrary = (): PersonalMediaLibraryContextType => {
  const context = useContext(PersonalMediaLibraryContext);
  if (!context) throw new Error('usePersonalMediaLibrary must be used within a PersonalMediaLibraryProvider');
  return context;
};
